import { useState, useEffect, memo } from 'react';
import {
  generatePlaceholder,
  extractDomain,
  detectContentType,
  extractYouTubeId,
} from '../../lib/utils';
import { useClient } from '../../context/ClientContext';
import { speaksPortuguese, useCopy } from '../../lib/copy';

/**
 * SourceCard - Progressive enhancement pattern for citations
 *
 * Per spec (html-conci-v4.html):
 * Phase 1: Render basic link immediately (fast!)
 * Phase 2: Progressively enrich with rich card (staggered 300ms)
 *
 * Handles: Articles, PDFs, Videos with intelligent fallbacks
 * Tags shown only on hover for minimal clutter
 * Quality metadata (content_type, content_authority, doc_date) shown in hover tooltip
 */
function SourceCard({
  url,
  title,
  summary,
  tags = [],
  index = 0,
  content_type,
  content_authority,
  doc_date,
  isDead = false,
}) {
  const t = useCopy();
  const { clientConfig } = useClient();
  // content_type and tags are English retrieval keys with no translation, so only
  // an English-speaking tenant's visitors are shown them.
  const showMetadataKeys = !speaksPortuguese(clientConfig?.locale);
  const [isEnriched, setIsEnriched] = useState(false);

  // Progressive enhancement: enrich after mount with stagger
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsEnriched(true);
    }, 300 * index); // Stagger by 300ms per card

    return () => clearTimeout(timer);
  }, [index]);

  const contentType = detectContentType(url);
  const domain = extractDomain(url);

  // Phase 1: Basic link (instant render)
  if (!isEnriched) {
    return (
      <div
        className={`rounded-lg border px-2 py-1.5 ${isDead ? 'border-slate-200/60 bg-slate-50/50' : 'border-slate-200'}`}
        data-testid="source-card"
      >
        {isDead ? (
          <span className="text-[13px] text-slate-400 line-through">{title}</span>
        ) : (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[13px] text-indigo-600 hover:text-indigo-700 font-medium"
          >
            {title} →
          </a>
        )}
        <p className="text-[10px] text-slate-400 mt-0.5">
          {isDead ? t('Page no longer available') : t('Loading preview...')}
        </p>
      </div>
    );
  }

  // Phase 2: Rich card (progressively enhanced)
  return (
    <div
      className="relative group/card scroll-mt-4"
      id={`source-${index + 1}`}
      data-testid="source-card"
    >
      <div
        className={`rounded-lg border transition-all ${isDead ? 'border-slate-200/60 bg-slate-50/50 opacity-60' : 'border-slate-200 hover:border-indigo-300 group-hover/card:bg-indigo-50/30'}`}
      >
        {isDead ? (
          <div className="flex gap-2 px-2 py-1.5">
            <SourceThumbnail url={url} title={title} contentType={contentType} />
            <div className="flex-1 min-w-0">
              <h4 className="text-[13px] font-medium text-slate-400 line-through line-clamp-1">
                {title}
              </h4>
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <span className="text-[10px] text-red-400 font-medium">
                  {t('Page no longer available')}
                </span>
                <span className="text-slate-300">·</span>
                <span className="text-[10px] text-slate-400">{domain}</span>
              </div>
            </div>
          </div>
        ) : (
          <a href={url} target="_blank" rel="noopener noreferrer" className="block group">
            <div className="flex gap-2 px-2 py-1.5">
              {/* Thumbnail */}
              <SourceThumbnail url={url} title={title} contentType={contentType} />

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h4 className="text-[13px] font-medium text-slate-900 group-hover:text-indigo-700 line-clamp-1">
                  {title}
                </h4>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                    {contentType === 'document'
                      ? t('Document')
                      : contentType === 'video'
                        ? t('Video')
                        : t('Article')}
                  </span>
                  <span className="text-slate-300">·</span>
                  <span className="text-[10px] text-slate-500">{domain}</span>
                </div>
              </div>

              {/* External link icon */}
              <svg
                className="w-3 h-3 text-slate-400 group-hover:text-indigo-600 shrink-0 mt-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </div>
          </a>
        )}
      </div>

      {/* Summary & Tags - hover tooltip (desktop only, appears ABOVE to avoid overflow clipping) */}
      {/* Always show tooltip - at minimum displays domain info */}
      <div className="hidden md:block absolute left-0 bottom-full mb-1 max-w-[50%] opacity-0 invisible group-hover/card:opacity-100 group-hover/card:visible transition-all duration-200 z-10 pointer-events-none">
        <div className="bg-white border border-indigo-200 text-slate-700 text-xs rounded-lg px-4 py-3 shadow-lg">
          {/* Summary if available */}
          {summary && <p className="leading-relaxed">{summary}</p>}

          {/* Source info - always show domain, plus quality context if available */}
          <div className={summary ? 'mt-2 pt-2 border-t border-slate-100' : ''}>
            <div className="flex items-center gap-2 text-[10px] text-slate-500 flex-wrap">
              {/* Always show domain */}
              <span className="flex items-center gap-1">🔗 {domain}</span>
              {showMetadataKeys && content_type && (
                <span className="flex items-center gap-1">📄 {content_type}</span>
              )}
              {content_authority === 'official' && (
                <span className="flex items-center gap-1 text-emerald-600 font-medium">
                  ✓ {t('Official')}
                </span>
              )}
              {content_authority === 'verified' && (
                <span className="flex items-center gap-1 text-blue-600 font-medium">
                  ✓ {t('Verified')}
                </span>
              )}
              {doc_date && (
                <span>
                  {t('Updated')} {new Date(doc_date).getFullYear()}
                </span>
              )}
            </div>
          </div>

          {/* Tags on hover */}
          {showMetadataKeys && tags && tags.length > 0 && (
            <div className="mt-2 pt-2 border-t border-slate-100">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">🏷️ Tags</p>
              <div className="flex flex-wrap gap-1">
                {tags.map((tag, i) => {
                  const isTopicTag = tag.startsWith('topic-');
                  return (
                    <span
                      key={i}
                      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium ${
                        isTopicTag
                          ? 'bg-amber-50 text-amber-700 border border-amber-200'
                          : 'bg-indigo-50 text-indigo-600 border border-indigo-100'
                      }`}
                    >
                      {tag}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Arrow pointing down (tooltip appears above) */}
          <div className="absolute -bottom-1 left-4 w-2 h-2 bg-white border-b border-r border-indigo-200 rotate-45"></div>
        </div>
      </div>
    </div>
  );
}

/**
 * SourceThumbnail - Renders appropriate thumbnail based on content type
 */
function SourceThumbnail({ url, title, contentType }) {
  const t = useCopy();
  const placeholderImg = generatePlaceholder(url, title, contentType);
  const [imgLoaded, setImgLoaded] = useState(false);

  if (contentType === 'video') {
    const videoId = extractYouTubeId(url);
    if (videoId) {
      return (
        <div className="shrink-0 w-12 h-9 rounded-md bg-slate-900 overflow-hidden relative">
          <img
            src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
            alt={t('Video thumbnail')}
            className="w-full h-full object-cover"
            style={{ opacity: imgLoaded ? 1 : 0, transition: 'opacity 0.3s' }}
            onLoad={() => setImgLoaded(true)}
          />
          {/* Play button overlay */}
          <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
            <div className="w-5 h-5 rounded-full bg-red-600 group-hover:bg-red-700 flex items-center justify-center transition-colors shadow-lg">
              <svg
                className="w-2.5 h-2.5 text-white ml-0.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </div>
      );
    }
  }

  // Article or PDF
  const bgColor = contentType === 'document' ? 'bg-red-50' : 'bg-slate-100';
  const borderColor = contentType === 'document' ? 'border-red-200' : 'border-slate-200';

  return (
    <div
      className={`shrink-0 w-12 h-9 rounded-md ${bgColor} overflow-hidden border ${borderColor}`}
    >
      <img
        src={placeholderImg}
        alt={contentType === 'document' ? t('PDF Document') : t('Article preview')}
        className="w-full h-full object-cover"
        style={{ opacity: imgLoaded ? 1 : 0, transition: 'opacity 0.3s' }}
        onLoad={() => setImgLoaded(true)}
        onError={() => setImgLoaded(true)} // Show even if image fails to load
      />
    </div>
  );
}

export default memo(SourceCard);
